/**
 * Polish water/environmental regulator inference from a postcode or region.
 *
 * Poland's water-permit authority is the national body
 *   Państwowe Gospodarstwo Wodne Wody Polskie (PGW WP — "Wody Polskie")
 * which issues water permits (pozwolenia wodnoprawne) through 11 Regional Water
 * Management Authorities:
 *   RZGW — Regionalne Zarządy Gospodarki Wodnej
 *   (Białystok, Bydgoszcz, Gdańsk, Gliwice, Kraków, Lublin, Poznań,
 *    Rzeszów, Szczecin, Warszawa, Wrocław)
 *
 * Environmental enforcement / inspection is carried out by the regional
 *   WIOŚ — Wojewódzki Inspektorat Ochrony Środowiska
 * (one per voivodeship), under the national GIOŚ.
 *
 * Polish postcodes are NN-NNN. The leading two digits map (approximately) to a
 * voivodeship, which in turn maps to its RZGW and WIOŚ. The mapping is indicative
 * for area-routing — RZGW boundaries follow river basins, not strictly admin lines.
 *
 * Returns the standard code used elsewhere in the app: 'PL_WP'.
 */

export type PolishRZGW =
  | 'RZGW_BIALYSTOK' | 'RZGW_BYDGOSZCZ' | 'RZGW_GDANSK' | 'RZGW_GLIWICE'
  | 'RZGW_KRAKOW' | 'RZGW_LUBLIN' | 'RZGW_POZNAN' | 'RZGW_RZESZOW'
  | 'RZGW_SZCZECIN' | 'RZGW_WARSZAWA' | 'RZGW_WROCLAW';

export interface PolishRegulatorResult {
  standard: 'PL_WP';
  voivodeship: string;       // województwo
  rzgw: PolishRZGW;          // Regional Water Management Authority (Wody Polskie)
  rzgwLabel: string;         // human label for the RZGW
  wios: string;              // regional environmental inspectorate (WIOŚ) seat
}

export const RZGW_LABEL: Record<PolishRZGW, string> = {
  RZGW_BIALYSTOK: 'Wody Polskie — RZGW Białystok',
  RZGW_BYDGOSZCZ: 'Wody Polskie — RZGW Bydgoszcz',
  RZGW_GDANSK:    'Wody Polskie — RZGW Gdańsk',
  RZGW_GLIWICE:   'Wody Polskie — RZGW Gliwice',
  RZGW_KRAKOW:    'Wody Polskie — RZGW Kraków',
  RZGW_LUBLIN:    'Wody Polskie — RZGW Lublin',
  RZGW_POZNAN:    'Wody Polskie — RZGW Poznań',
  RZGW_RZESZOW:   'Wody Polskie — RZGW Rzeszów',
  RZGW_SZCZECIN:  'Wody Polskie — RZGW Szczecin',
  RZGW_WARSZAWA:  'Wody Polskie — RZGW Warszawa',
  RZGW_WROCLAW:   'Wody Polskie — RZGW Wrocław',
};

/** Ordered list of RZGW body labels (for dropdowns). */
export const PL_RZGW_BODIES: string[] = Object.values(RZGW_LABEL);

/** National default body when the region cannot be resolved. */
export const PL_NATIONAL_BODY = 'Wody Polskie (PGW WP)';

interface VoivEntry { voivodeship: string; rzgw: PolishRZGW; wios: string; }

/** Resolve a voivodeship + RZGW + WIOŚ from the leading two postcode digits. */
function fromPostcodePrefix(n: number): VoivEntry | null {
  if (n >= 0  && n <= 9)  return { voivodeship: 'Mazowieckie',          rzgw: 'RZGW_WARSZAWA',  wios: 'WIOŚ Warszawa' };
  if (n >= 10 && n <= 14) return { voivodeship: 'Warmińsko-Mazurskie',  rzgw: 'RZGW_BIALYSTOK', wios: 'WIOŚ Olsztyn' };
  if (n >= 15 && n <= 19) return { voivodeship: 'Podlaskie',            rzgw: 'RZGW_BIALYSTOK', wios: 'WIOŚ Białystok' };
  if (n >= 20 && n <= 24) return { voivodeship: 'Lubelskie',            rzgw: 'RZGW_LUBLIN',    wios: 'WIOŚ Lublin' };
  if (n >= 25 && n <= 29) return { voivodeship: 'Świętokrzyskie',       rzgw: 'RZGW_KRAKOW',    wios: 'WIOŚ Kielce' };
  if (n >= 30 && n <= 34) return { voivodeship: 'Małopolskie',          rzgw: 'RZGW_KRAKOW',    wios: 'WIOŚ Kraków' };
  if (n >= 35 && n <= 39) return { voivodeship: 'Podkarpackie',         rzgw: 'RZGW_RZESZOW',   wios: 'WIOŚ Rzeszów' };
  if (n >= 40 && n <= 44) return { voivodeship: 'Śląskie',              rzgw: 'RZGW_GLIWICE',   wios: 'WIOŚ Katowice' };
  if (n >= 45 && n <= 49) return { voivodeship: 'Opolskie',             rzgw: 'RZGW_WROCLAW',   wios: 'WIOŚ Opole' };
  if (n >= 50 && n <= 59) return { voivodeship: 'Dolnośląskie',         rzgw: 'RZGW_WROCLAW',   wios: 'WIOŚ Wrocław' };
  if (n >= 60 && n <= 64) return { voivodeship: 'Wielkopolskie',        rzgw: 'RZGW_POZNAN',    wios: 'WIOŚ Poznań' };
  if (n >= 65 && n <= 69) return { voivodeship: 'Lubuskie',             rzgw: 'RZGW_WROCLAW',   wios: 'WIOŚ Zielona Góra' };
  if (n >= 70 && n <= 79) return { voivodeship: 'Zachodniopomorskie',   rzgw: 'RZGW_SZCZECIN',  wios: 'WIOŚ Szczecin' };
  if (n >= 80 && n <= 84) return { voivodeship: 'Pomorskie',            rzgw: 'RZGW_GDANSK',    wios: 'WIOŚ Gdańsk' };
  if (n >= 85 && n <= 89) return { voivodeship: 'Kujawsko-Pomorskie',   rzgw: 'RZGW_BYDGOSZCZ', wios: 'WIOŚ Bydgoszcz' };
  if (n >= 90 && n <= 99) return { voivodeship: 'Łódzkie',              rzgw: 'RZGW_WARSZAWA',  wios: 'WIOŚ Łódź' };
  return null;
}

/** Voivodeship name (lowercased, ascii-folded) → entry, for region-name matching. */
const VOIV_BY_NAME: Record<string, VoivEntry> = {
  'mazowieckie':        { voivodeship: 'Mazowieckie',         rzgw: 'RZGW_WARSZAWA',  wios: 'WIOŚ Warszawa' },
  'warminsko-mazurskie':{ voivodeship: 'Warmińsko-Mazurskie', rzgw: 'RZGW_BIALYSTOK', wios: 'WIOŚ Olsztyn' },
  'podlaskie':          { voivodeship: 'Podlaskie',           rzgw: 'RZGW_BIALYSTOK', wios: 'WIOŚ Białystok' },
  'lubelskie':          { voivodeship: 'Lubelskie',           rzgw: 'RZGW_LUBLIN',    wios: 'WIOŚ Lublin' },
  'swietokrzyskie':     { voivodeship: 'Świętokrzyskie',      rzgw: 'RZGW_KRAKOW',    wios: 'WIOŚ Kielce' },
  'malopolskie':        { voivodeship: 'Małopolskie',         rzgw: 'RZGW_KRAKOW',    wios: 'WIOŚ Kraków' },
  'podkarpackie':       { voivodeship: 'Podkarpackie',        rzgw: 'RZGW_RZESZOW',   wios: 'WIOŚ Rzeszów' },
  'slaskie':            { voivodeship: 'Śląskie',             rzgw: 'RZGW_GLIWICE',   wios: 'WIOŚ Katowice' },
  'opolskie':           { voivodeship: 'Opolskie',            rzgw: 'RZGW_WROCLAW',   wios: 'WIOŚ Opole' },
  'dolnoslaskie':       { voivodeship: 'Dolnośląskie',        rzgw: 'RZGW_WROCLAW',   wios: 'WIOŚ Wrocław' },
  'wielkopolskie':      { voivodeship: 'Wielkopolskie',       rzgw: 'RZGW_POZNAN',    wios: 'WIOŚ Poznań' },
  'lubuskie':           { voivodeship: 'Lubuskie',            rzgw: 'RZGW_WROCLAW',   wios: 'WIOŚ Zielona Góra' },
  'zachodniopomorskie': { voivodeship: 'Zachodniopomorskie',  rzgw: 'RZGW_SZCZECIN',  wios: 'WIOŚ Szczecin' },
  'pomorskie':          { voivodeship: 'Pomorskie',           rzgw: 'RZGW_GDANSK',    wios: 'WIOŚ Gdańsk' },
  'kujawsko-pomorskie': { voivodeship: 'Kujawsko-Pomorskie',  rzgw: 'RZGW_BYDGOSZCZ', wios: 'WIOŚ Bydgoszcz' },
  'lodzkie':            { voivodeship: 'Łódzkie',             rzgw: 'RZGW_WARSZAWA',  wios: 'WIOŚ Łódź' },
};

function fold(s: string): string {
  return s.toLowerCase()
    .replace(/ą/g,'a').replace(/ć/g,'c').replace(/ę/g,'e').replace(/ł/g,'l')
    .replace(/ń/g,'n').replace(/ó/g,'o').replace(/ś/g,'s').replace(/ż/g,'z').replace(/ź/g,'z')
    .trim();
}

export function inferPolishRegulator(postcodeOrRegion?: string | null): PolishRegulatorResult {
  const fallback: PolishRegulatorResult = {
    standard: 'PL_WP', voivodeship: 'Mazowieckie',
    rzgw: 'RZGW_WARSZAWA', rzgwLabel: RZGW_LABEL.RZGW_WARSZAWA, wios: 'WIOŚ Warszawa',
  };
  if (!postcodeOrRegion) return fallback;
  const raw = postcodeOrRegion.trim();

  // Postcode NN-NNN (or NNNNN) → leading two digits
  const pc = raw.match(/(\d{2})\s*-?\s*\d{3}/);
  if (pc) {
    const entry = fromPostcodePrefix(parseInt(pc[1], 10));
    if (entry) return { standard: 'PL_WP', ...entry, rzgwLabel: RZGW_LABEL[entry.rzgw] };
  }

  // Voivodeship / city name match
  const folded = fold(raw);
  for (const [name, entry] of Object.entries(VOIV_BY_NAME)) {
    if (folded.includes(name)) return { standard: 'PL_WP', ...entry, rzgwLabel: RZGW_LABEL[entry.rzgw] };
  }
  // A few common city anchors
  const cityHints: Array<[RegExp, keyof typeof VOIV_BY_NAME]> = [
    [/warszaw|warsaw/, 'mazowieckie'], [/krakow|cracow/, 'malopolskie'], [/wroclaw/, 'dolnoslaskie'],
    [/poznan/, 'wielkopolskie'], [/gdansk|gdynia|sopot/, 'pomorskie'], [/szczecin/, 'zachodniopomorskie'],
    [/lublin/, 'lubelskie'], [/katowic|gliwic|sosnowiec/, 'slaskie'], [/lodz/, 'lodzkie'],
    [/bialystok/, 'podlaskie'], [/rzeszow/, 'podkarpackie'], [/bydgoszcz|torun/, 'kujawsko-pomorskie'],
    [/olsztyn/, 'warminsko-mazurskie'], [/kielce/, 'swietokrzyskie'], [/opole/, 'opolskie'],
    [/zielona gora|gorzow/, 'lubuskie'],
  ];
  for (const [re, key] of cityHints) {
    if (re.test(folded)) { const e = VOIV_BY_NAME[key]; return { standard: 'PL_WP', ...e, rzgwLabel: RZGW_LABEL[e.rzgw] }; }
  }
  return fallback;
}
